import React from 'react';
import { LanguageLink } from '../components/LanguageLink';
import { useI18n } from "../i18n/i18n";
import { useSEO } from '../hooks/useSEO';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ScrollToTopButton from '../components/ScrollToTopButton';
import ArticlesCarousel from '../components/ArticlesCarousel';
import {
  LineChart,
  ArrowRight,
  DollarSign,
  BarChartHorizontal,
  Lock,
  HomeIcon,
  PiggyBank,
  TrendingUp,
  Users,
  Target,
} from "lucide-react";

const HomePage = () => {
  const { t } = useI18n();

  // SEO Configuration
  const seoContent = useSEO('main', 'home');

  return (
    <div className="fade-in">
      {seoContent}
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#F9FAFB] py-16 md:py-24">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#FB923C]/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#EA580C]/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-neutral-800 leading-tight mb-6 hero-title">
                {t("hero.title")}
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 mb-8 leading-relaxed">
                {t("hero.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <LanguageLink to="/tools">
                  <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all">
                    {t("hero.cta")}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </LanguageLink>
                <LanguageLink to="/resources">
                  <Button 
                    variant="outline" 
                    className="border-2 border-neutral-300 text-neutral-700 hover:border-[#EA580C] hover:text-[#EA580C] font-medium px-8 py-4 rounded-lg transition-all"
                  >
                    {t("hero.ctaSecondary")}
                  </Button>
                </LanguageLink>
              </div>
            </div>
            <div className="relative h-64 md:h-auto">
              <picture className="md:hidden">
                <source type="image/avif" srcSet="/images/brand/hero-video-poster.avif" />
                <source type="image/webp" srcSet="/images/brand/hero-video-poster.webp" />
                <img 
                  src="/images/brand/hero-video-poster.jpg" 
                  alt="Finanzly Hero" 
                  className="w-full h-full object-cover rounded-2xl shadow-2xl border-2 border-white"
                />
              </picture>
              <video
                poster="/images/brand/hero-video-poster.jpg"
                autoPlay
                muted
                playsInline
                loop={false}
                preload="metadata"
                className="hidden md:block w-full h-full object-cover rounded-2xl shadow-2xl video-element opacity-0 border-2 border-white"
                onLoadedMetadata={(e) => {
                  // Once metadata is loaded, we can start downloading the full video
                  const video = e.currentTarget;
                  video.preload = "auto";
                }}
                onCanPlayThrough={(e) => {
                  // Video is fully loaded and can play without buffering
                  const video = e.currentTarget;
                  // Add a slight delay before showing and playing to ensure smooth transition
                  setTimeout(() => {
                    video.classList.remove("opacity-0");
                    video.classList.add("fade-in");
                    video.play().catch(() => {});
                  }, 100);
                }}
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;
                  // When approaching the end (last second), start fading out
                  if (video.duration - video.currentTime < 1) {
                    video.classList.add("fade-out");
                    video.classList.remove("fade-in");
                  }
                }}
                onEnded={(e) => {
                  const video = e.currentTarget;
                  // Apply fade-out effect
                  video.classList.add("opacity-0");

                  // After fading out, reset position and fade back in
                  setTimeout(() => {
                    video.currentTime = 0;
                    video.classList.remove("opacity-0");
                    video.classList.add("fade-in");
                    video.classList.remove("fade-out");
                    video.play().catch(() => {});
                  }, 1000);
                }}
                onError={(e) => {
                  console.error("Video error:", e);
                }}
              >
                <source src="/hero-video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>

      {/* Value Pillars Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
              {t("home.valuePillars.title")}
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              {t("home.valuePillars.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Pillar 1 - Smart Tools */}
            <div className="text-center group p-6 rounded-xl hover:bg-neutral-50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-[#EA580C] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <LineChart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">
                {t("home.valuePillars.pillar1.title")}
              </h3>
              <p className="text-neutral-600">
                {t("home.valuePillars.pillar1.description")}
              </p>
            </div>

            {/* Pillar 2 - Financial Education */}
            <div className="text-center group p-6 rounded-xl hover:bg-neutral-50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-[#EA580C] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">
                {t("home.valuePillars.pillar2.title")}
              </h3>
              <p className="text-neutral-600">
                {t("home.valuePillars.pillar2.description")}
              </p>
            </div>

            {/* Pillar 3 - Personalized Planning */}
            <div className="text-center group p-6 rounded-xl hover:bg-neutral-50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-[#EA580C] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <PiggyBank className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">
                {t("home.valuePillars.pillar3.title")}
              </h3>
              <p className="text-neutral-600">
                {t("home.valuePillars.pillar3.description")}
              </p>
            </div>
          </div>

          {/* Compound Interest Hook */}
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-8 md:p-10">
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                {t("home.hook.title")}
              </h2>
              
              <p className="text-base md:text-lg text-neutral-600 mb-8 max-w-2xl mx-auto">
                {t("home.hook.description")}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <LanguageLink to="/tools/compound-interest">
                  <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-lg transform">
                    {t("home.hook.ctaPrimary")}
                  </Button>
                </LanguageLink>
                <LanguageLink to="/resources">
                  <Button
                    variant="outline"
                    className="border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-50 font-semibold px-8 py-3 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-lg transform"
                  >
                    {t("home.hook.ctaSecondary")}
                  </Button>
                </LanguageLink>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tools Carousel */}
      <div className="bg-[#F9FAFB] py-16">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-12">
            {t("home.tools.title")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Tool Card 1 */}
            <Card className="tool-card bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-[#FB923C]/20 flex items-center justify-center mb-4">
                  <LineChart className="h-6 w-6 text-[#EA580C]" />
                </div>
                <h3 className="font-semibold text-xl mb-2">
                  {t("tools.compound.title")}
                </h3>
                <p className="text-neutral-600 mb-4">
                  {t("tools.compound.shortDesc")}
                </p>
                <LanguageLink to="/tools/compound-interest">
                  <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                    {t("home.tools.useTool")}{" "}
                    <ArrowRight className="inline h-4 w-4 ml-1" />
                  </div>
                </LanguageLink>
              </CardContent>
            </Card>

            {/* Tool Card 2 */}
            <Card className="tool-card bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-[#FB923C]/20 flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-[#EA580C]" />
                </div>
                <h3 className="font-semibold text-xl mb-2">
                  {t("tools.salary.title")}
                </h3>
                <p className="text-neutral-600 mb-4">
                  {t("tools.salary.shortDesc")}
                </p>
                <LanguageLink to="/tools/net-salary">
                  <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                    {t("home.tools.useTool")}{" "}
                    <ArrowRight className="inline h-4 w-4 ml-1" />
                  </div>
                </LanguageLink>
              </CardContent>
            </Card>

            {/* Tool Card - Monthly Savings Calculator */}
            <Card className="tool-card bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-[#FB923C]/20 flex items-center justify-center mb-4">
                  <PiggyBank className="h-6 w-6 text-[#EA580C]" />
                </div>
                <h3 className="font-semibold text-xl mb-2">
                  {t("tools.savings.title")}
                </h3>
                <p className="text-neutral-600 mb-4">
                  {t("tools.savings.shortDesc")}
                </p>
                <LanguageLink to="/tools/monthly-savings-calculator">
                  <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                    {t("home.tools.useTool")}{" "}
                    <ArrowRight className="inline h-4 w-4 ml-1" />
                  </div>
                </LanguageLink>
              </CardContent>
            </Card>

            {/* Tool Card 3 - Mortgage Calculator (Premium) */}
            <Card className="tool-card bg-white rounded-xl shadow-md overflow-hidden relative">
              <div className="absolute top-4 right-4 bg-[#EA580C] text-white text-xs px-2 py-1 rounded-full">
                {t("common.premium")}
              </div>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-[#FB923C]/20 flex items-center justify-center mb-4">
                  <HomeIcon className="h-6 w-6 text-[#EA580C]" />
                </div>
                <h3 className="font-semibold text-xl mb-2">
                  {t("tools.mortgage.title")}
                </h3>
                <p className="text-neutral-600 mb-4">
                  {t("tools.mortgage.description")}
                </p>
                <LanguageLink to="/tools/mortgage-calculator">
                  <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                    {t("home.tools.useTool")}{" "}
                    <ArrowRight className="inline h-4 w-4 ml-1" />
                  </div>
                </LanguageLink>
              </CardContent>
            </Card>

            {/* Tool Card 4 - Salary Comparison Calculator */}
            <Card className="tool-card bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-[#FB923C]/20 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-[#EA580C]" />
                </div>
                <h3 className="font-semibold text-xl mb-2">
                  {t("tools.salaryComparison.title")}
                </h3>
                <p className="text-neutral-600 mb-4">
                  {t("tools.salaryComparison.shortDesc")}
                </p>
                <LanguageLink to="/tools/salary-comparison">
                  <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                    {t("home.tools.useTool")}{" "}
                    <ArrowRight className="inline h-4 w-4 ml-1" />
                  </div>
                </LanguageLink>
              </CardContent>
            </Card>

            {/* Tool Card 5 (Future Tool - Premium) */}
            <Card className="tool-card bg-white rounded-xl shadow-md overflow-hidden relative">
              <div className="absolute top-4 right-4 bg-neutral-800 text-white text-xs px-2 py-1 rounded-full">
                {t("common.comingSoon")}
              </div>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-neutral-200 flex items-center justify-center mb-4">
                  <BarChartHorizontal className="h-6 w-6 text-neutral-400" />
                </div>
                <h3 className="font-semibold text-xl mb-2">
                  {t("tools.portfolio.title")}
                </h3>
                <p className="text-neutral-600 mb-4">
                  {t("tools.portfolio.description")}
                </p>
                <span className="inline-block text-neutral-400 font-medium">
                  {t("common.premium")} <Lock className="inline h-4 w-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Articles Carousel Section */}
      <ArticlesCarousel />

      {/* Savings Section - New SEO Optimized Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 mb-4">
              {t("home.savings.title")}
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto leading-relaxed">
              {t("home.savings.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-[#F0F9FF] to-white p-6 rounded-xl border border-[#3B82F6]/20 hover:shadow-lg transition-all">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-[#3B82F6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-6 w-6 text-[#3B82F6]" />
                </div>
                <h3 className="font-semibold text-xl mb-2 text-neutral-800">
                  {t("home.savings.feature1.title")}
                </h3>
              </div>
              <p className="text-neutral-600 leading-relaxed">
                {t("home.savings.feature1.description")}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-[#F0FDF4] to-white p-6 rounded-xl border border-[#10B981]/20 hover:shadow-lg transition-all">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-[#10B981]" />
                </div>
                <h3 className="font-semibold text-xl mb-2 text-neutral-800">
                  {t("home.savings.feature2.title")}
                </h3>
              </div>
              <p className="text-neutral-600 leading-relaxed">
                {t("home.savings.feature2.description")}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-[#FEF3F2] to-white p-6 rounded-xl border border-[#EA580C]/20 hover:shadow-lg transition-all">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-[#EA580C]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-6 w-6 text-[#EA580C]" />
                </div>
                <h3 className="font-semibold text-xl mb-2 text-neutral-800">
                  {t("home.savings.feature3.title")}
                </h3>
              </div>
              <p className="text-neutral-600 leading-relaxed">
                {t("home.savings.feature3.description")}
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LanguageLink to="/tools/monthly-savings-calculator">
                <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium px-8 py-3 rounded-lg shadow-md hover:shadow-lg">
                  {t("home.savings.cta")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </LanguageLink>
              <LanguageLink to="/resources/emergency-fund-article">
                <Button
                  variant="outline"
                  className="border-2 border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white font-medium px-8 py-3 rounded-lg transition-colors"
                >
                  {t("home.savings.ctaSecondary")}
                </Button>
              </LanguageLink>
            </div>
          </div>
        </div>
      </div>

      

      {/* Getting Started Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 mb-4">
              {t("home.gettingStarted.title")}
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              {t("home.gettingStarted.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-gradient-to-br from-[#FFF7ED] to-white p-6 rounded-xl border border-[#FB923C]/20 hover:shadow-lg transition-all">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#EA580C] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <h4 className="font-semibold text-lg text-neutral-800">{t("home.gettingStarted.step1.title")}</h4>
              </div>
              <p className="text-neutral-600 text-center">
                {t("home.gettingStarted.step1.description")}
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-gradient-to-br from-[#FFF7ED] to-white p-6 rounded-xl border border-[#FB923C]/20 hover:shadow-lg transition-all">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#EA580C] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <h4 className="font-semibold text-lg text-neutral-800">{t("home.gettingStarted.step2.title")}</h4>
              </div>
              <p className="text-neutral-600 text-center">
                {t("home.gettingStarted.step2.description")}
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-gradient-to-br from-[#FFF7ED] to-white p-6 rounded-xl border border-[#FB923C]/20 hover:shadow-lg transition-all">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#EA580C] to-[#FB923C] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <h4 className="font-semibold text-lg text-neutral-800">{t("home.gettingStarted.step3.title")}</h4>
              </div>
              <p className="text-neutral-600 text-center">
                {t("home.gettingStarted.step3.description")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#EA580C] to-[#FB923C] py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {t("home.cta.title")}
            </h2>
            <p className="text-lg mb-8 opacity-90">
              {t("home.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LanguageLink to="/tools">
                <Button className="bg-white text-[#EA580C] hover:bg-gray-100 font-medium px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all glow-button">
                  {t("home.cta.primary")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </LanguageLink>
              <LanguageLink to="/pricing">
                <Button 
                  variant="outline" 
                  className="border-2 border-white bg-white/10 text-white hover:bg-white hover:text-[#EA580C] font-medium px-8 py-4 rounded-lg transition-all backdrop-blur-sm"
                >
                  {t("home.cta.secondary")}
                </Button>
              </LanguageLink>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .tool-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .tool-card:hover {
            transform: translateY(-5px);
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .fade-in {
            animation: fadeIn 0.5s ease forwards;
          }

          .video-element {
            transition: opacity 0.8s ease;
            max-height: 100%;
            width: 100%;
          }

          .fade-in {
            animation: videoFadeIn 0.8s ease-in forwards;
          }

          .fade-out {
            animation: videoFadeOut 0.8s ease-out forwards;
          }

          /* Animations for stats */
          @keyframes countUp {
            from { opacity: 0; transform: scale(0.5); }
            to { opacity: 1; transform: scale(1); }
          }

          @keyframes statGlow {
            0% { 
              transform: scale(1);
              box-shadow: 0 0 0 rgba(234, 88, 12, 0);
            }
            50% { 
              transform: scale(1.05);
              box-shadow: 0 0 20px rgba(234, 88, 12, 0.3);
            }
            100% { 
              transform: scale(1);
              box-shadow: 0 0 0 rgba(234, 88, 12, 0);
            }
          }

          .stat-number {
            animation: countUp 0.8s ease-out forwards;
          }

          .stat-restart {
            animation: statGlow 0.6s ease-in-out;
          }

          /* Gradient text effect */
          .gradient-text {
            background: linear-gradient(45deg, #EA580C, #FB923C);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          /* Pulse animation for badges */
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }

          .pulse-animation {
            animation: pulse 2s infinite;
          }

          /* Success story cards hover effect */
          .success-card {
            transition: all 0.3s ease;
            transform-origin: center;
          }

          .success-card:hover {
            transform: translateY(-8px) scale(1.02);
            box-shadow: 0 20px 40px rgba(234, 88, 12, 0.15);
          }

          /* Floating animation for background decorations */
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-10px) rotate(1deg); }
            66% { transform: translateY(5px) rotate(-1deg); }
          }

          .floating {
            animation: float 6s ease-in-out infinite;
          }

          /* CTA button glow effect */
          .glow-button {
            position: relative;
            overflow: hidden;
          }

          .glow-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: left 0.5s;
          }

          .glow-button:hover::before {
            left: 100%;
          }

          /* Mobile-specific styles */
          @media (max-width: 768px) {
            .video-element {
              /* Ensure video is visible on mobile */
              height: 100%;
              object-fit: cover;
            }
            
            .hero-title {
              font-size: 2rem !important;
              line-height: 1.2;
            }
          }

          @keyframes videoFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes videoFadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          /* Scroll reveal animation */
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(50px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .scroll-reveal {
            animation: slideInUp 0.8s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};

export default HomePage;
